import { Effect } from 'effect';

import {
  defineDocumentParser,
  type ParsePageInput,
  type ParsedPage,
  type ParserDefinition,
  type ParserError,
  type ParserNetworkPolicy,
  type RequirementsOf,
} from './parser-layer';
import type { ParserTier } from './parser';

const tierRank: Readonly<Record<ParserTier, number>> = { fast: 0, standard: 1, premium: 2 };

function derivedSpec(definitions: readonly ParserDefinition<any>[]) {
  const first = definitions[0];
  if (!first) throw new Error('parser combinators require at least one parser');
  const ids = definitions.map((definition) => definition.spec.id);
  const hosts = definitions.flatMap((definition) =>
    definition.spec.network === 'none' ? [] : definition.spec.network.allowedHosts,
  );
  const network: ParserNetworkPolicy =
    hosts.length === 0 ? 'none' : { allowedHosts: [...new Set(hosts)] };
  const tier = definitions.reduce(
    (current, definition) =>
      tierRank[definition.spec.tier] > tierRank[current] ? definition.spec.tier : current,
    'fast' as ParserTier,
  );
  return {
    ...first.spec,
    id: ids.join('+'),
    name: definitions.map((definition) => definition.spec.name).join(' + '),
    tier,
    requiredEnvKeys: [
      ...new Set(definitions.flatMap((definition) => definition.spec.requiredEnvKeys)),
    ],
    inputs: [...new Set(definitions.flatMap((definition) => definition.spec.inputs))],
    outputs: [...new Set(definitions.flatMap((definition) => definition.spec.outputs))],
    properties: definitions.flatMap((definition) => definition.spec.properties),
    provides: [...new Set(definitions.flatMap((definition) => definition.spec.provides))],
    network,
  };
}

/** Try the secondary parser when the primary fails with an accepted ParserError. */
export function fallbackParser<R1, R2>(
  primary: ParserDefinition<R1>,
  secondary: ParserDefinition<R2>,
  options: { readonly when?: (error: ParserError) => boolean } = {},
): ParserDefinition<R1 | R2> {
  return defineDocumentParser({
    spec: derivedSpec([primary, secondary]),
    available: Effect.zipWith(
      primary.available,
      secondary.available,
      (left, right) => left || right,
    ),
    parsePage: (input) =>
      primary
        .parsePage(input)
        .pipe(
          Effect.catch((error) =>
            options.when === undefined || options.when(error)
              ? secondary.parsePage(input)
              : Effect.fail(error),
          ),
        ),
    resolveType: (rawType) => primary.resolveType?.(rawType) ?? secondary.resolveType?.(rawType),
  });
}

type ParserMap<K extends string> = { readonly [P in K]: ParserDefinition<any> };
type RequirementsOfMap<P extends ParserMap<string>> = RequirementsOf<P[keyof P]>;

/** Route each page to one definition without adding a parser implementation. */
export function routeParser<K extends string, P extends ParserMap<K>, RR>(options: {
  readonly route: (input: ParsePageInput) => Effect.Effect<K, ParserError, RR>;
  readonly parsers: P;
}): ParserDefinition<RequirementsOfMap<P> | RR> {
  const definitions = Object.values(options.parsers) as ParserDefinition<any>[];
  return defineDocumentParser({
    spec: derivedSpec(definitions),
    available: Effect.forEach(definitions, (definition) => definition.available).pipe(
      Effect.map((availability) => availability.some(Boolean)),
    ),
    parsePage: (input) =>
      options.route(input).pipe(
        Effect.flatMap((key) => {
          const parser = options.parsers[key];
          return parser.parsePage(input);
        }),
      ),
    resolveType: (rawType) =>
      definitions
        .find((definition) => definition.resolveType?.(rawType) !== undefined)
        ?.resolveType?.(rawType),
  });
}

/** Run all definitions for a page and merge their ParsedPage results. */
export function mergeParser<Defs extends readonly ParserDefinition<any>[]>(
  parsers: Defs,
  merge: (pages: readonly ParsedPage[]) => ParsedPage,
): ParserDefinition<RequirementsOf<Defs[number]>> {
  return defineDocumentParser({
    spec: derivedSpec(parsers),
    available: Effect.forEach(parsers, (definition) => definition.available).pipe(
      Effect.map((availability) => availability.every(Boolean)),
    ),
    parsePage: (input) =>
      Effect.forEach(parsers, (definition) => definition.parsePage(input), {
        concurrency: 'unbounded',
      }).pipe(Effect.map(merge)),
    resolveType: (rawType) =>
      parsers
        .find((definition) => definition.resolveType?.(rawType) !== undefined)
        ?.resolveType?.(rawType),
  });
}
